import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CourseCard } from "@/components/CourseCard";
import { Seo } from "@/components/Seo";
import { Search, SlidersHorizontal } from "lucide-react";

const SELECTS = [
  { key: "category", label: "Category", opts: [] },
  { key: "delivery", label: "Delivery", opts: ["Online", "In-person", "Hybrid"] },
  { key: "level", label: "Level", opts: ["Beginner", "Intermediate", "Advanced"] },
  { key: "format", label: "Format", opts: ["Intensive", "Weekend", "Evening", "Weekday"] },
];

export default function Catalogue() {
  const { category: routeCategory } = useParams();
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState({ q: "", category: "All", delivery: "All", level: "All", format: "All", funding: false });
  const [meta, setMeta] = useState({ categories: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/courses/meta/filters").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setFilters((f) => ({ ...f, category: routeCategory || "All" }));
    window.scrollTo(0, 0);
  }, [routeCategory]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== "All" && v !== false) params[k] = v;
    });
    const { data } = await api.get("/courses", { params });
    setCourses(data);
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10" data-testid="catalogue-page">
      <Seo
        title={routeCategory ? `${routeCategory} courses` : "Browse all courses"}
        description={routeCategory ? `Browse all ${routeCategory} courses — practical, CPD-accredited online training from Get Skilled in Days Not Years.` : "Browse practical, CPD-accredited online courses. Short, career-focused training you can start today."}
        path={routeCategory ? `/courses/category/${encodeURIComponent(routeCategory)}` : "/courses"}
      />
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mb-2">{routeCategory ? routeCategory : "Find your course"}</h1>
      <p className="text-[var(--ink-2)] mb-6">{routeCategory ? `Browse all ${routeCategory} courses.` : "Short courses. Serious opportunities."}</p>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="category-chips">
        <Link to="/courses" className={`text-sm font-semibold px-3.5 py-1.5 border ${!routeCategory ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)] hover:border-[var(--ink)]"}`}>All</Link>
        {meta.categories.map((c) => (
          <Link key={c} to={`/courses/category/${encodeURIComponent(c)}`} data-testid={`chip-${c}`} className={`text-sm font-semibold px-3.5 py-1.5 border ${routeCategory === c ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "border-[var(--line)] hover:border-[var(--ink)]"}`}>{c}</Link>
        ))}
      </div>

      {/* Search + filters */}
      <div className="border border-[var(--line)] bg-white p-4 mb-8 space-y-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-2)]" />
          <input
            data-testid="search-input"
            value={filters.q}
            onChange={(e) => set("q", e.target.value)}
            placeholder="Search bootcamps, skills, keywords…"
            className="w-full pl-10 pr-4 py-3 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SlidersHorizontal size={16} className="text-[var(--ink-2)] hidden sm:block" />
          {SELECTS.map((s) => (
            <select
              key={s.key}
              data-testid={`filter-${s.key}`}
              value={filters[s.key]}
              onChange={(e) => set(s.key, e.target.value)}
              className="border border-[var(--line)] px-3 py-2 text-sm focus:border-[var(--accent)] outline-none bg-white"
            >
              <option value="All">{s.label}: All</option>
              {(s.key === "category" ? meta.categories : s.opts).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="filter-funding">
            <input type="checkbox" checked={filters.funding} onChange={(e) => set("funding", e.target.checked)} className="accent-[var(--accent)]" />
            Funding available
          </label>
        </div>
      </div>

      <p className="text-sm text-[var(--ink-2)] mb-5" data-testid="results-count">{loading ? "Loading…" : `${courses.length} course${courses.length === 1 ? "" : "s"}`}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {courses.map((c) => <CourseCard key={c.id} course={c} />)}
      </div>
      {!loading && courses.length === 0 && (
        <div className="text-center py-20 text-[var(--ink-2)]" data-testid="no-results">No courses match your filters.</div>
      )}
    </div>
  );
}
